// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Wordloom — scoring. Each scoring LINE (a row, a column, and in Deluge a diagonal)
// contributes the highest-scoring valid word it contains as a contiguous run of 3+
// letters. A word's value is (Σ tile values) × a steep length multiplier, so a long
// word is the prize. The whole scoring surface is pure so the mechanism audit
// (tests/mechanism.test.ts) can re-derive it from independent constants.

import { isWord, tileValue } from './dict';
import type { LineSet, Mode } from './modes';

/** Steep so length is rewarded: a 6-letter word is worth far more than two 3s. */
export const LEN_MULT: Readonly<Record<number, number>> = { 3: 1, 4: 1.6, 5: 2.4, 6: 3.4 };

/** Raw score of a specific word string (validity is the caller's concern). */
export function wordScore(word: string): number {
  const w = word.toUpperCase();
  let sum = 0;
  for (const ch of w) sum += tileValue(ch);
  return Math.round(sum * (LEN_MULT[w.length] ?? 0));
}

export interface BestWord {
  word: string;
  score: number;
  /** Offset within the line where the word starts. */
  start: number;
  len: number;
}

/**
 * The best valid word in one line of cells (nulls are gaps that break contiguity).
 * Returns null if the line holds no valid 3+ word.
 */
export function bestWordInLine(cells: readonly (string | null)[]): BestWord | null {
  let best: BestWord | null = null;
  const n = cells.length;
  for (let start = 0; start < n; start++) {
    if (cells[start] == null) continue;
    let run = '';
    for (let end = start; end < n; end++) {
      const ch = cells[end];
      if (ch == null) break;
      run += ch;
      if (run.length >= 3 && isWord(run)) {
        const sc = wordScore(run);
        if (!best || sc > best.score || (sc === best.score && run.length > best.len)) {
          best = { word: run, score: sc, start, len: run.length };
        }
      }
    }
  }
  return best;
}

export type LineKind = 'row' | 'col' | 'diag';

export interface LineDef {
  kind: LineKind;
  /** row index / col index; for diagonals: 0 = main (↘), 1 = anti (↙). */
  index: number;
  /** Flat cell indices that make up this line, in reading order. */
  cellIdx: number[];
}

/** The set of scoring lines for a mode's geometry. */
export function linesFor(size: number, set: LineSet): LineDef[] {
  const out: LineDef[] = [];
  for (let r = 0; r < size; r++) {
    out.push({ kind: 'row', index: r, cellIdx: Array.from({ length: size }, (_, c) => r * size + c) });
  }
  for (let c = 0; c < size; c++) {
    out.push({ kind: 'col', index: c, cellIdx: Array.from({ length: size }, (_, r) => r * size + c) });
  }
  if (set === 'rcd') {
    out.push({ kind: 'diag', index: 0, cellIdx: Array.from({ length: size }, (_, i) => i * size + i) });
    out.push({ kind: 'diag', index: 1, cellIdx: Array.from({ length: size }, (_, i) => i * size + (size - 1 - i)) });
  }
  return out;
}

export interface LineScore extends LineDef {
  best: BestWord | null;
  /** Flat cell indices covered by this line's best word (for highlighting). */
  wordCells: number[];
}

export interface GridScore {
  total: number;
  lines: LineScore[];
  /** Flat cell index → which axes it scores in ('row'/'col'/'diag'), for the renderer. */
  cellKinds: Map<number, Set<LineKind>>;
}

/** Score a whole grid for a mode. `cells` is row-major, length size². */
export function scoreGrid(cells: readonly (string | null)[], mode: Mode): GridScore {
  const defs = linesFor(mode.size, mode.lines);
  const cellKinds = new Map<number, Set<LineKind>>();
  let total = 0;
  const lines: LineScore[] = defs.map((def) => {
    const letters = def.cellIdx.map((i) => cells[i]);
    const best = bestWordInLine(letters);
    let wordCells: number[] = [];
    if (best) {
      total += best.score;
      wordCells = def.cellIdx.slice(best.start, best.start + best.len);
      for (const ci of wordCells) {
        const set = cellKinds.get(ci) ?? new Set<LineKind>();
        set.add(def.kind);
        cellKinds.set(ci, set);
      }
    }
    return { ...def, best, wordCells };
  });
  return { total, lines, cellKinds };
}
